import { atom } from 'jotai';
import { mutationAtomWithLoadable } from 'lib/atoms';
import request from 'lib/request';
import type {
  CategoriesPhaseResponse,
  OverAllocatedSlot,
  RunAuditResponse,
  SlotDetailResponse,
  SlotKindFilter,
  SlotsPhaseResponse,
} from './types';

/** Which grain the list is showing: LTD slots, well slots, or both. */
export const kindAtom = atom<SlotKindFilter>('both');

/**
 * Starts an audit. The report is built off the request — the roll-up scans
 * nri_calculations whole and classification reaches subtract geometry for every
 * contributing row, which together exceeded the request budget.
 */
export const [runAuditAtom, runAuditLoadableAtom] = mutationAtomWithLoadable<
  RunAuditResponse,
  [SlotKindFilter]
>(async (_get, _set, kind: SlotKindFilter) => {
  const abortController = new AbortController();
  const response = await request.post<RunAuditResponse, { error: string }>(
    '/admin/over_allocated_slots/run',
    { kind },
    abortController
  );

  if (response.type === 'error') {
    throw new Error(response.data?.error ?? 'Failed to start the audit');
  }

  if (response.type === 'fatal' || response.type === 'abort') {
    throw new Error(response.message);
  }

  return (response as { type: 'success'; data: RunAuditResponse }).data;
});

/**
 * Reads one finished phase. `ready: false` means the phase has not landed yet
 * (or failed) — distinct from a phase that finished with nothing in it.
 *
 * @param {string} auditId - The audit to read.
 * @param {string} phase - Which half of the report to read.
 * @return {Promise<object>} The phase payload.
 */
const fetchPhase = async <T extends object>(
  auditId: string,
  phase: string
): Promise<T> => {
  const response = await request.get<T>(
    `/admin/over_allocated_slots/results/${auditId}?phase=${phase}`
  );
  if ('error' in response) {
    throw new Error((response as { error: string }).error);
  }
  return response as T;
};

export const fetchSlotsPhase = (auditId: string) =>
  fetchPhase<SlotsPhaseResponse>(auditId, 'slots');

export const fetchCategoriesPhase = (auditId: string) =>
  fetchPhase<CategoriesPhaseResponse>(auditId, 'categories');

// Drill-down is fetched per expanded row rather than shipped with the list:
// a slot carries up to ~400 contributions, and only a handful get opened. It is
// scoped to one slot, so it stays a direct request rather than going through the
// audit.
export const fetchSlotDetail = async (
  slot: OverAllocatedSlot
): Promise<SlotDetailResponse> => {
  const params = new URLSearchParams({ kind: slot.kind });
  if (slot.kind === 'ltd') {
    params.set('slot_id', String(slot.slotId));
  } else {
    params.set('well_uwi', String(slot.wellUWI));
    params.set('unit_tract_id', String(slot.developmentAreaId));
  }

  const response = await request.get<SlotDetailResponse>(
    `/admin/over_allocated_slots/detail?${params.toString()}`
  );
  if ('error' in response) {
    throw new Error(response.error);
  }
  return response;
};
